import React, { useEffect, useMemo, useState } from 'react';
import Plot from 'react-plotly.js';

// Panel "Gasto semanal por alumno" → botón "Comparar con el año pasado".
// Se ve de una en una: primero la Media y luego cada supervisora (flechas ← → del
// teclado, botones ‹ › o pulsando su nombre). En cada paso:
//   - Media: media este año / media año pasado
//   - Supervisora X: además, X este año / X año pasado
// El año pasado es la misma semana del curso (lo calcula el backend: pasado.weeks).

const NEGRO = '#111827';
const GRIS = '#94a3b8';

const ddmm = (iso) => (iso ? `${iso.slice(8, 10)}/${iso.slice(5, 7)}` : '');
const ddmmaa = (iso) => (iso ? `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(2, 4)}` : '');
const eur = (v) => (v === null || v === undefined ? '—' : `${Number(v).toFixed(2).replace('.', ',')} €`);

// Media de las semanas con dato en los dos años (para comparar lo mismo con lo mismo)
const mediasComunes = (actual, pasado) => {
  const a = [], p = [];
  (actual || []).forEach((v, i) => {
    const w = pasado ? pasado[i] : null;
    if (v !== null && v !== undefined && w !== null && w !== undefined) { a.push(v); p.push(w); }
  });
  const m = (xs) => (xs.length ? xs.reduce((x, y) => x + y, 0) / xs.length : null);
  return { actual: m(a), pasado: m(p), semanas: a.length };
};

const Variacion = ({ a, p }) => {
  if (a === null || p === null || !p) return null;
  const pct = (a / p - 1) * 100;
  const sube = pct > 0.05, baja = pct < -0.05;
  return (
    <span style={{
      fontWeight: 800, fontSize: 13, borderRadius: 999, padding: '2px 9px', whiteSpace: 'nowrap',
      color: sube ? '#b91c1c' : baja ? '#15803d' : '#64748b', background: sube ? '#fee2e2' : baja ? '#dcfce7' : '#f1f5f9',
    }}>
      {sube ? '▲ +' : baja ? '▼ ' : '= '}{pct.toFixed(1).replace('.', ',')}%
    </span>
  );
};

const Linea = ({ color, discontinua }) => (
  <svg width="26" height="8" style={{ flexShrink: 0 }}>
    <line x1="0" y1="4" x2="26" y2="4" stroke={color} strokeWidth="3" strokeDasharray={discontinua ? '6 4' : undefined} />
  </svg>
);

const Valor = ({ color, discontinua, titulo, valor }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0' }}>
    <Linea color={color} discontinua={discontinua} />
    <span style={{ flex: 1, fontSize: 14, color: '#334155' }}>{titulo}</span>
    <b style={{ fontSize: 17, color: '#0f172a', fontVariantNumeric: 'tabular-nums' }}>{eur(valor)}</b>
  </div>
);

const Bloque = ({ titulo, color, m }) => (
  <div style={{ flex: '1 1 280px', border: '1px solid #e2e8f0', borderRadius: 14, padding: '10px 14px', background: '#fff' }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 }}>
      <span style={{ flex: 1, fontWeight: 900, fontSize: 15, color: '#0f172a' }}>{titulo}</span>
      <Variacion a={m.actual} p={m.pasado} />
    </div>
    <Valor color={color} titulo="Este año" valor={m.actual} />
    <Valor color={color === NEGRO ? GRIS : color} discontinua titulo="Año pasado" valor={m.pasado} />
    <div style={{ fontSize: 12, color: '#94a3b8' }}>
      {m.semanas ? `Media de ${m.semanas} semana${m.semanas === 1 ? '' : 's'} con datos en los dos años` : 'Sin semanas con datos en los dos años'}
    </div>
  </div>
);

export default function ComparadorAnioPasado({ weeks, mean, series, pasado, colorMap }) {
  const pasoSeries = useMemo(() => series.map((s) => s.name), [series]);
  const pasos = useMemo(() => ['__media__', ...pasoSeries], [pasoSeries]);
  const [idx, setIdx] = useState(0);
  useEffect(() => { if (idx >= pasos.length) setIdx(0); }, [pasos, idx]);

  const mover = (d) => setIdx((i) => (i + d + pasos.length) % pasos.length);

  // ← → del teclado (no cuando se está escribiendo en un campo)
  useEffect(() => {
    const onKey = (e) => {
      const t = e.target && e.target.tagName;
      if (t === 'INPUT' || t === 'SELECT' || t === 'TEXTAREA') return;
      if (e.key === 'ArrowRight') { e.preventDefault(); setIdx((i) => (i + 1) % pasos.length); }
      if (e.key === 'ArrowLeft') { e.preventDefault(); setIdx((i) => (i - 1 + pasos.length) % pasos.length); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [pasos.length]);

  const actual = pasos[idx] && pasos[idx] !== '__media__' ? pasos[idx] : null;
  const color = actual ? (colorMap[actual] || '#2563eb') : NEGRO;
  const serieA = actual ? series.find((s) => s.name === actual) : null;
  const serieP = actual ? (pasado.series || []).find((s) => s.name === actual) : null;
  const valsP = serieP ? serieP.values : weeks.map(() => null);

  const mMedia = mediasComunes(mean.values, pasado.mean);
  const mSup = actual ? mediasComunes(serieA ? serieA.values : [], valsP) : null;
  const sinPasado = actual && !valsP.some((v) => v !== null && v !== undefined);

  const x = weeks.map(ddmm);
  const custom = weeks.map((w, i) => ddmmaa(pasado.weeks[i]));
  const traza = (nombre, y, col, discontinua, ancho, semanaPasada) => ({
    x, y, name: nombre, type: 'scatter', mode: 'lines+markers', connectgaps: false,
    line: { color: col, width: ancho, dash: discontinua ? 'dash' : 'solid' }, marker: { size: discontinua ? 6 : 8, color: col },
    customdata: custom,
    hovertemplate: `${nombre}${semanaPasada ? ' (sem. %{customdata})' : ''}: <b>%{y:.2f} €</b><extra></extra>`,
  });
  const datos = [
    traza('Media año pasado', pasado.mean, GRIS, true, actual ? 2 : 3, true),
    traza('Media este año', mean.values, NEGRO, false, actual ? 2 : 4, false),
  ];
  if (actual) {
    datos[0].opacity = 0.55;
    datos[1].opacity = 0.45;
    datos.push(traza(`${actual} año pasado`, valsP, color, true, 3, true));
    datos.push(traza(`${actual} este año`, serieA ? serieA.values : [], color, false, 4, false));
  }

  const botonFlecha = (d, txt) => (
    <button onClick={() => mover(d)} aria-label={d < 0 ? 'Anterior' : 'Siguiente'} style={{
      width: 44, height: 44, borderRadius: 999, border: '1px solid #cbd5e1', background: '#fff', cursor: 'pointer',
      fontSize: 22, fontWeight: 900, color: '#334155', boxShadow: '0 2px 8px rgba(0,0,0,0.06)', flexShrink: 0,
    }}>{txt}</button>
  );

  return (
    <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 14, padding: 14 }}>
      {/* Navegación */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
        {botonFlecha(-1, '‹')}
        <div style={{ flex: 1, textAlign: 'center' }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 21, fontWeight: 900, color: '#0f172a' }}>
            <span style={{ width: 14, height: 14, borderRadius: 4, background: color, display: 'inline-block' }} />
            {actual || 'Media'}
          </div>
          <div style={{ fontSize: 12.5, color: '#64748b', marginTop: 2 }}>
            {idx + 1} de {pasos.length} · usa las flechas ← → del teclado o los botones
          </div>
        </div>
        {botonFlecha(1, '›')}
      </div>

      {/* Saltar directamente */}
      <div style={{ display: 'flex', gap: 6, overflowX: 'auto', paddingBottom: 6, marginBottom: 8 }}>
        {pasos.map((p, i) => {
          const sel = i === idx;
          const col = p === '__media__' ? NEGRO : (colorMap[p] || '#2563eb');
          return (
            <button key={p} onClick={() => setIdx(i)} style={{
              flexShrink: 0, display: 'flex', alignItems: 'center', gap: 6, padding: '5px 11px', borderRadius: 999, cursor: 'pointer',
              border: sel ? `2px solid ${col}` : '1px solid #e2e8f0', background: sel ? '#f8fafc' : '#fff',
              fontWeight: sel ? 900 : 600, fontSize: 13, color: '#0f172a',
            }}>
              <span style={{ width: 9, height: 9, borderRadius: 3, background: col }} />
              {p === '__media__' ? 'Media' : p}
            </button>
          );
        })}
      </div>

      {/* Resumen */}
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 6 }}>
        <Bloque titulo="Media" color={NEGRO} m={mMedia} />
        {actual && <Bloque titulo={actual} color={color} m={mSup} />}
      </div>
      {sinPasado && (
        <div style={{ fontSize: 13, color: '#92400e', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 10, padding: '6px 10px', marginBottom: 6 }}>
          {actual} no tenía compras el año pasado en estas semanas.
        </div>
      )}

      <Plot
        data={datos}
        layout={{
          autosize: true, height: 400, margin: { l: 56, r: 16, t: 10, b: 50 }, hovermode: 'x unified',
          legend: { orientation: 'h', y: 1.12, x: 0 }, paper_bgcolor: '#fff', plot_bgcolor: '#fff',
          yaxis: { gridcolor: '#eef2f7', zeroline: false, ticksuffix: ' €', rangemode: 'tozero' },
          xaxis: { gridcolor: '#f8fafc', title: { text: 'Semana (este año)', font: { size: 12, color: '#64748b' } } },
        }}
        style={{ width: '100%' }} useResizeHandler config={{ displaylogo: false, responsive: true }}
      />
      <div style={{ fontSize: 12.5, color: '#64748b' }}>
        Línea continua = este año · discontinua = la misma semana del curso pasado (fecha al pasar el ratón).
      </div>
    </div>
  );
}
